import replaceSlugs from "@utils/replaceSlugs"

import { ObjectsData } from "@organisms/ObjectsView/ObjectsView.types"
import { GetObjectDetailsRouteFunction } from "@organisms/Table/TableViewControls/TableViewTab/TableViewTab.types"

import { PRICEBOOK_ITEM_TYPE_URL_PARAM_MAP } from "@pages/Pricebook/PricebookList/views/PricebookTableView/PricebookTableView.utils"

import { BILLS_ROUTES } from "@routes/bills"
import { CATALOG_ROUTES } from "@routes/catalogs"
import { CLIENTS_ROUTES } from "@routes/clients"
import { CREDIT_NOTES_ROUTES } from "@routes/creditNotes"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { JOB_SERIES_ROUTES } from "@routes/jobs-series"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import useHasAccess from "./useHasAccess"
import useSlugExtractor from "./useSlugExtractor"

export type ObjectsKeysMap = {
    [key in ObjectIndexName]: GetObjectDetailsRouteFunction
}

export default function useObjectDetailsRoutes(
    objectName?: ObjectIndexName,
): ObjectsKeysMap | GetObjectDetailsRouteFunction {
    const [
        estimateUpdateRoute,
        estimateDetailsRoute,
        jobUpdateRoute,
        jobDetailsRoute,
        jobSeriesDetailsRoute,
        invoiceUpdateRoute,
        invoiceDetailsRoute,
        creditNoteUpdateRoute,
        creditNoteDetailsRoute,
        billUpdateRoute,
        billDetailsRoute,
        purchaseOrderUpdateRoute,
        purchaseOrderDetailsRoute,
        vendorDetailsRoute,
        clientDetailsRoute,
        pricebookItemUpdateRoute,
        pricebookItemReviewRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.UPDATE,
        ESTIMATES_ROUTES.DETAILS,
        JOBS_ROUTES.UPDATE,
        JOBS_ROUTES.DETAILS,
        JOB_SERIES_ROUTES.DETAILS,
        INVOICES_ROUTES.UPDATE,
        INVOICES_ROUTES.DETAILS,
        CREDIT_NOTES_ROUTES.UPDATE,
        CREDIT_NOTES_ROUTES.DETAILS,
        BILLS_ROUTES.UPDATE,
        BILLS_ROUTES.DETAILS,
        PURCHASE_ORDER_ROUTES.UPDATE,
        PURCHASE_ORDER_ROUTES.DETAILS,
        VENDOR_ROUTES.DETAILS,
        CLIENTS_ROUTES.DETAILS,
        PRICEBOOK_ITEMS_ROUTES.UPDATE,
        PRICEBOOK_ITEMS_ROUTES.REVIEW,
    ])

    const { hasPermission } = useHasAccess()

    const getObjectDetailsRoute: {
        [key in ObjectIndexName]: (object: ObjectsData | ObjectsIndex) => string
    } = {
        Estimate: (obj: ObjectsData | ObjectsIndex) => {
            const estimate = obj as Estimate | EstimateIndex
            const pathname = "is_draft" in estimate && estimate.is_draft ? estimateUpdateRoute : estimateDetailsRoute
            return replaceSlugs(pathname, { id: estimate.id })
        },
        Job: (obj: ObjectsData | ObjectsIndex) => {
            const job = obj as Job | JobIndex
            const pathname =
                "is_draft" in job && job.is_draft && hasPermission("jobs_edit_permission", PERMISSION_LEVEL.RESTRICTED)
                    ? jobUpdateRoute
                    : jobDetailsRoute

            return replaceSlugs(pathname, { id: job.id })
        },
        Invoice: (obj: ObjectsData | ObjectsIndex) => {
            const invoice = obj as Invoice | InvoiceIndex
            const pathname =
                "is_draft" in invoice &&
                invoice.is_draft &&
                hasPermission("invoices_edit_permission", PERMISSION_LEVEL.RESTRICTED)
                    ? invoiceUpdateRoute
                    : invoiceDetailsRoute
            return replaceSlugs(pathname, { id: invoice.id })
        },
        CreditNote: (obj: ObjectsData | ObjectsIndex) => {
            const creditNote = obj as unknown as CreditNote
            const pathname =
                "is_draft" in creditNote &&
                creditNote.is_draft &&
                hasPermission("invoices_edit_permission", PERMISSION_LEVEL.RESTRICTED)
                    ? creditNoteUpdateRoute
                    : creditNoteDetailsRoute
            return replaceSlugs(pathname, { id: creditNote.id })
        },
        PurchaseOrder: (obj: ObjectsData | ObjectsIndex) => {
            const purchaseOrder = obj as PurchaseOrder | PurchaseOrderIndex
            const pathname =
                "is_draft" in purchaseOrder &&
                purchaseOrder.is_draft &&
                hasPermission("purchase_orders_edit_permission", PERMISSION_LEVEL.RESTRICTED)
                    ? purchaseOrderUpdateRoute
                    : purchaseOrderDetailsRoute
            return replaceSlugs(pathname, { id: purchaseOrder.id })
        },
        Bill: (obj: ObjectsData | ObjectsIndex) => {
            const bill = obj as Bill | BillIndex
            const pathname =
                "is_draft" in bill &&
                bill.is_draft &&
                hasPermission("bills_edit_permission", PERMISSION_LEVEL.RESTRICTED)
                    ? billUpdateRoute
                    : billDetailsRoute
            return replaceSlugs(pathname, { id: bill.id })
        },
        Vendor: (obj: ObjectsData | ObjectsIndex) => {
            const vendor = obj as Vendor | VendorIndex
            return replaceSlugs(vendorDetailsRoute, { id: vendor.id })
        },
        JobSeries: (obj: ObjectsData | ObjectsIndex) => {
            const series = obj as JobSeries | JobSeriesIndex
            return replaceSlugs(jobSeriesDetailsRoute, { id: series.id })
        },
        Client: (obj: ObjectsData | ObjectsIndex) => {
            const client = obj as Client | ClientIndex
            return replaceSlugs(clientDetailsRoute, { id: client.id })
        },
        PriceBookItem: (obj: ObjectsData | ObjectsIndex) => {
            const pricebookItem = obj as PricebookItem | PricebookItemIndex
            const pathname = pricebookItem.confirmed ? pricebookItemUpdateRoute : pricebookItemReviewRoute
            const query = `?type=${PRICEBOOK_ITEM_TYPE_URL_PARAM_MAP[pricebookItem.pricebook_item_type]}`
            return `${replaceSlugs(pathname, { id: String(pricebookItem.id) })}${query}`
        },
        CatalogItem: (obj: ObjectsData | ObjectsIndex) => {
            return replaceSlugs(CATALOG_ROUTES.ITEM_DETAILS, { id: String(obj.id) })
        },
    }

    if (objectName) {
        return getObjectDetailsRoute[objectName]
    } else {
        return getObjectDetailsRoute
    }
}
